/**
 * Copyright (C) 2019 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import React, { useEffect, useMemo } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { withTheme } from 'styled-components';
import GoogleMapsControl, { MapControlOrderIndex } from '../map/GoogleMapsControl';
import { SvgIconProps } from '@mui/material/SvgIcon';
import Error from '@mui/icons-material/Error';
import TrackChanges from '@mui/icons-material/TrackChanges';
import StyledProps from '../theme/AppThemeInterface';
import styled from 'styled-components';
import {
  selectIsRequestingTailDetailsPanelTerminal,
  selectRequestTailDetailsPanelTerminalError,
  selectSelectedDatapathUuidInPlatformList,
  selectTerminalDetails,
  selectTerminalsInBeamLength,
  selectTerminalsInYourBeamPanelShown,
} from '../../../store';
import { toggleTerminalsInYourBeamPanel } from '../../../store/ui/actions';
import { DISPLAY_BLOCK_CLASS, DISPLAY_NONE_CLASS } from '../map/BaseMapWrapper';
import { TerminalDetails } from '../../../shared/entities/terminal';
import { requireFeatureFlag, FEATURE_FLAGS } from '../../../utils/FeatureFlagUtils';

const TERMINALS_IN_BEAM_BADGE_COUNT_MAX = 99;

interface TerminalsInBeamMapControlProps extends StyledProps {
  map: google.maps.Map;
  isFullScreen: boolean;
  isFullTailView: boolean;
}

export function TerminalsInBeamMapControl({
  theme,
  map,
  isFullScreen,
  isFullTailView,
}: TerminalsInBeamMapControlProps) {
  const dispatch = useDispatch();

  const terminalsInYourBeamControlCountBadge = React.useRef<HTMLDivElement>(null);
  const terminalsInYourBeamControlErrorBadge = React.useRef<HTMLDivElement>(null);
  const terminalsInYourBeamControlIcon = React.useRef<HTMLDivElement>(null);

  const terminalsInBeamCount = useSelector(selectTerminalsInBeamLength, shallowEqual);
  const tailDetailsPanelTerminal = useSelector(selectTerminalDetails, shallowEqual);
  const isRequestingTailDetailsPanelTerminal = useSelector(selectIsRequestingTailDetailsPanelTerminal, shallowEqual);
  const requestTailDetailsPanelTerminalError = useSelector(selectRequestTailDetailsPanelTerminalError, shallowEqual);
  const selectedDatapathUuidInPlatformList = useSelector(selectSelectedDatapathUuidInPlatformList, shallowEqual);
  const terminalsInYourBeamPanelShown = useSelector(selectTerminalsInYourBeamPanelShown, shallowEqual);

  const items = useMemo(() => {
    return [
      {
        render: () => (
          <React.Fragment>
            <div
              className="terminals_in_your_beam_button_panel_btn_count_badge"
              data-test="terminals_in_your_beam_button_panel_btn_count_badge"
              ref={terminalsInYourBeamControlCountBadge}
            >
              {terminalsInBeamCount}
            </div>
            <div
              className="terminals_in_your_beam_button_panel_btn_error_badge"
              data-test="terminals_in_your_beam_button_panel_btn_error_badge"
              ref={terminalsInYourBeamControlErrorBadge}
            >
              <StyledErrorBackground />
              <StyledError />
            </div>
            <span ref={terminalsInYourBeamControlIcon}>
              <StyledRadar id="terminals_in_your_beam_button_panel_btn" />
            </span>
          </React.Fragment>
        ),
        buttonTextColor: theme.secondaryButtonTextColor,
        id: 'terminals_in_your_beam_button_panel_btn_container',
        onClick: () => {
          dispatch(toggleTerminalsInYourBeamPanel());
        },
      },
    ];
  }, [
    terminalsInBeamCount,
    dispatch,
    terminalsInYourBeamControlCountBadge,
    terminalsInYourBeamControlErrorBadge,
    terminalsInYourBeamControlIcon,
    theme,
  ]);

  useEffect(() => {
    determineTerminalsInBeamBadgeVisibility(
      terminalsInYourBeamControlCountBadge,
      terminalsInYourBeamControlErrorBadge,
      isRequestingTailDetailsPanelTerminal,
      requestTailDetailsPanelTerminalError,
      isFullTailView,
      isFullScreen,
      tailDetailsPanelTerminal,
      selectedDatapathUuidInPlatformList,
      terminalsInBeamCount,
    );

    if (terminalsInYourBeamControlIcon.current && terminalsInYourBeamControlIcon.current.classList) {
      if (terminalsInYourBeamPanelShown) {
        terminalsInYourBeamControlIcon.current.classList.add('terminals_in_your_beam_button_panel_btn--active');
      } else {
        terminalsInYourBeamControlIcon.current.classList.remove('terminals_in_your_beam_button_panel_btn--active');
      }
    }
  }, [
    terminalsInBeamCount,
    isFullScreen,
    isFullTailView,
    isRequestingTailDetailsPanelTerminal,
    requestTailDetailsPanelTerminalError,
    selectedDatapathUuidInPlatformList,
    tailDetailsPanelTerminal,
    terminalsInYourBeamPanelShown,
  ]);

  return (
    <GoogleMapsControl
      items={items}
      map={map}
      mapPosition={google.maps.ControlPosition.RIGHT_BOTTOM}
      height={32}
      hidden={isFullScreen || !isFullTailView}
      index={MapControlOrderIndex.TERMINALS_IN_BEAM_CONTROL}
    />
  );
}

export default requireFeatureFlag(withTheme(TerminalsInBeamMapControl), FEATURE_FLAGS.isTerminalInBeamEnabled);

function Radar(props: SvgIconProps) {
  return <TrackChanges transform="scale(1, -1) rotate(45.0)" {...props} />;
}

const StyledRadar = styled(Radar)`
  height: 20px !important;
  width: 20px !important;
  margin-top: 6px !important;
`;

const StyledError = styled(Error)`
  position: absolute;
  color: #6f777b;
  font-size: 27px !important;
  top: -10px;
  left: -10px;
  display: inline-block;
  z-index: 5;
`;

const StyledErrorBackground = styled.div`
  position: absolute;
  background-color: white;
  z-index: 4;
  height: 20px;
  width: 20px;
  top: -7px;
  left: -6px;
  border-radius: 15px;
`;

/*
    This function is responsible for determining (based on props and class variables) which classes to apply to the Terminals In Beam
    badge in order for it to be displayed or hidden
  
    - The badge is hidden on any route that is not the Full Tail View or if the map is in full screen
    - The badge is hidden while the Tail Details request is in progress
    - An error badge is displayed when the request for the Tail Details fails
   */
export const determineTerminalsInBeamBadgeVisibility = (
  terminalsInYourBeamControlCountBadge: React.RefObject<HTMLDivElement> | undefined,
  terminalsInYourBeamControlErrorBadge: React.RefObject<HTMLDivElement> | undefined,
  isRequestingTailDetailsPanelTerminal: boolean,
  requestTailDetailsPanelTerminalError: string | undefined,
  isFullTailView: boolean,
  isFullscreen: boolean,
  tailDetailsPanelTerminal: TerminalDetails | undefined,
  selectedDatapathUuidInPlatformList: string | undefined,
  terminalsInYourBeamListCount: number,
) => {
  if (
    terminalsInYourBeamControlCountBadge &&
    terminalsInYourBeamControlCountBadge.current &&
    terminalsInYourBeamControlErrorBadge &&
    terminalsInYourBeamControlErrorBadge.current
  ) {
    if (
      !isRequestingTailDetailsPanelTerminal &&
      !requestTailDetailsPanelTerminalError &&
      isFullTailView &&
      !isFullscreen &&
      tailDetailsPanelTerminal &&
      tailDetailsPanelTerminal.terminalsInBeam &&
      tailDetailsPanelTerminal.datapathUuid === selectedDatapathUuidInPlatformList
    ) {
      /*
          Show the count badge and hide the error badge because:
            1. There request to get the Tail Details succeeded
            2. The map is not full screen
            3. The current route is the Full Tail View
        */
      updateTerminalsInBeamCountBadgeInnerHtml(
        terminalsInYourBeamListCount,
        terminalsInYourBeamControlCountBadge.current,
      );
      showTerminalsInBeamCountBadge(terminalsInYourBeamControlCountBadge.current);
      hideTerminalsInBeamErrorBadge(terminalsInYourBeamControlErrorBadge.current);
    } else if (
      requestTailDetailsPanelTerminalError &&
      !isRequestingTailDetailsPanelTerminal &&
      isFullTailView &&
      !isFullscreen
    ) {
      /*
          Hide the count badge and show the error badge because:
            1. There request to get the Tail Details failed
            2. The map is full screen
            3. The current route is the Full Tail View
        */
      hideTerminalsInBeamCountBadge(terminalsInYourBeamControlCountBadge.current);
      showTerminalsInBeamErrorBadge(terminalsInYourBeamControlErrorBadge.current);
    } else {
      /*
          Hide the count badge and hide the error badge because:
            1. The request to get the Tail Details is in progress
            2. The map is full screen
            3. The current route is not the Full Tail View
        */
      hideTerminalsInBeamCountBadge(terminalsInYourBeamControlCountBadge.current);
      hideTerminalsInBeamErrorBadge(terminalsInYourBeamControlErrorBadge.current);
    }
  }
};

/*
    This function is responsible for manually setting the innerHTML of the terminals in your beam badge to the number of terminals in your beam
  
    - The google maps control with the aircraft button and terminals count badge is created once and is not hooked up to the react lifecycle.
    - Upon creating the HTML Div Element containing the badge a class variable React Ref is created that points to that HTML Div Element
    - In the component did update react lifecycle if is determined that there has been a change in the number of terminals in the beam or the
      selected tail changes this method will take in the terminals in beam and update the HTML Div Element that the ref points to and set
      the inner html to the length of the array
  
    NOTE: Terminals that have been offline for more than an hour are filtered out and not included in the count
  
   */
const updateTerminalsInBeamCountBadgeInnerHtml = (
  // The array of terminals in beam on the tail details object
  numTerminalsInBeam: number,
  // The React Ref pointing to the HTML Div Element that is the badge as part of the Google Maps control
  terminalsInYourBeamControlCountBadgeCurrent: HTMLDivElement,
): void => {
  // If the array of terminals in beam is set, filter out the terminals that have been offline more than an hour
  if (numTerminalsInBeam > TERMINALS_IN_BEAM_BADGE_COUNT_MAX) {
    // If there are more than 99 terminals in the beam display 99+
    terminalsInYourBeamControlCountBadgeCurrent.innerHTML = `${TERMINALS_IN_BEAM_BADGE_COUNT_MAX}+`;
  } else {
    // Else set the number in the badge to the count of terminals in the beam that have not been offline more than an hour
    terminalsInYourBeamControlCountBadgeCurrent.innerHTML = `${numTerminalsInBeam}`;
  }
};

/*
    This function is responsible for showing the badge. The badge should be shown based on the following criteria
    - The Current route is the Full Tail View
    - The request for the Tail Details Object has finished
  
    It takes in the Ref to the HTML Div Element that is the badge as part of the Google Maps control and
    - Adds a class with css applying: display: block
    - Removes a class with css applying: display: none
   */
const showTerminalsInBeamCountBadge = (
  // The React Ref pointing to the HTML Div Element that is the badge as part of the Google Maps control
  terminalsInYourBeamControlCountBadgeCurrent: HTMLDivElement,
): void => {
  terminalsInYourBeamControlCountBadgeCurrent.classList.add(DISPLAY_BLOCK_CLASS);
  terminalsInYourBeamControlCountBadgeCurrent.classList.remove(DISPLAY_NONE_CLASS);
};

/*
    This function is responsible for hiding the badge. The badge hidden be shown based on the following criteria
    - The current route is not the Full Tail View
    - When the request for the Tail Details object is in progress
  
    It takes in the Ref to the HTML Div Element that is the badge as part of the Google Maps control and
    - Adds a class with css applying: display: none
    - Removes a class with css applying: display: block
   */
const hideTerminalsInBeamCountBadge = (
  // The React Ref pointing to the HTML Div Element that is the badge as part of the Google Maps control
  terminalsInYourBeamControlCountBadgeCurrent: HTMLDivElement,
): void => {
  terminalsInYourBeamControlCountBadgeCurrent.classList.add(DISPLAY_NONE_CLASS);
  terminalsInYourBeamControlCountBadgeCurrent.classList.remove(DISPLAY_BLOCK_CLASS);
};

/*
    This function is responsible for showing the error badge. The badge should be shown based on the following criteria
    - The request to get the Tail Details succeeds
  
    It takes in the Ref to the HTML Div Element that is the badge as part of the Google Maps control and
    - Adds a class with css applying: display: block
    - Removes a class with css applying: display: none
   */
const showTerminalsInBeamErrorBadge = (
  // The React Ref pointing to the HTML Div Element that is the badge as part of the Google Maps control
  terminalsInYourBeamControlErrorBadgeCurrent: HTMLDivElement,
): void => {
  terminalsInYourBeamControlErrorBadgeCurrent.classList.add(DISPLAY_BLOCK_CLASS);
  terminalsInYourBeamControlErrorBadgeCurrent.classList.remove(DISPLAY_NONE_CLASS);
};

/*
    This function is responsible for hiding the error badge. The badge should be hidden based on the following criteria
    - The request to get the Terminal Details fails
  
    It takes in the Ref to the HTML Div Element that is the badge as part of the Google Maps control and
    - Adds a class with css applying: display: none
    - Removes a class with css applying: display: block
   */
const hideTerminalsInBeamErrorBadge = (
  // The React Ref pointing to the HTML Div Element that is the badge as part of the Google Maps control
  terminalsInYourBeamControlErrorBadgeCurrent: HTMLDivElement,
): void => {
  terminalsInYourBeamControlErrorBadgeCurrent.classList.add(DISPLAY_NONE_CLASS);
  terminalsInYourBeamControlErrorBadgeCurrent.classList.remove(DISPLAY_BLOCK_CLASS);
};
